import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import GeniLoader from '../../components/GeniLoader';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { POSITION_BIAS_SEGMENTS, getFuturesRiskRows, getPortfolioExposureRows, getPositionRiskRows, getWalletExposureItems } from './optionsPortfolioRiskUtils';

const getBiasLabel = (bias, t) => (bias?.labelKey ? t(bias.labelKey) : t('positions.awaitingValidation'));

const buildSectionSummary = ({ rows = [], getBias, t }) => {
  if (!rows.length) return t('positions.noExposureSummary');
  return rows.map((row) => `${row.currency} ${getBiasLabel(getBias(row), t)}`).join(' · ');
};

export const OptionsPortfolioRisk = ({ accountData, loading = false, underlyingFilter = 'all' }) => {
  const { t } = useOptionsLocale();
  const [walletExpanded, setWalletExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const walletItems = useMemo(() => getWalletExposureItems(accountData), [accountData]);
  const positionRows = useMemo(() => getPositionRiskRows(accountData), [accountData]);
  const futuresRows = useMemo(() => getFuturesRiskRows(accountData), [accountData]);
  const portfolioRows = useMemo(() => getPortfolioExposureRows(accountData), [accountData]);
  const demoMeta = accountData?.portfolio_risk_demo || null;
  const normalizedFilter = String(underlyingFilter || 'all').toUpperCase();

  const activeCurrencies = useMemo(() => {
    const currencies = new Set();
    portfolioRows.forEach((row) => {
      const walletVal = Number(row.wallet?.nativeValue || 0);
      const optionsVal = Number(row.options?.netDeltaValue || row.position?.netDeltaValue || 0);
      const futuresVal = Number(row.futures?.netDeltaValue || 0);
      const portfolioVal = Number(row.portfolio?.nativeValue || 0);
      
      if (walletVal !== 0 || optionsVal !== 0 || futuresVal !== 0 || portfolioVal !== 0) {
        if (row.currency) {
          currencies.add(String(row.currency).toUpperCase());
        }
      }
    });
    return currencies;
  }, [portfolioRows]);

  const visibleWalletItems = useMemo(() => {
    const filteredByAsset = normalizedFilter === 'ALL'
      ? walletItems
      : walletItems.filter((item) => String(item.currency).toUpperCase() === normalizedFilter);
    return filteredByAsset.filter((item) => activeCurrencies.has(String(item.currency).toUpperCase()));
  }, [walletItems, normalizedFilter, activeCurrencies]);

  const visiblePositionRows = useMemo(() => {
    const filteredByAsset = normalizedFilter === 'ALL'
      ? positionRows
      : positionRows.filter((row) => String(row.currency).toUpperCase() === normalizedFilter);
    return filteredByAsset.filter((row) => activeCurrencies.has(String(row.currency).toUpperCase()));
  }, [positionRows, normalizedFilter, activeCurrencies]);

  const visiblePortfolioRows = useMemo(() => {
    const filteredByAsset = normalizedFilter === 'ALL'
      ? portfolioRows
      : portfolioRows.filter((row) => String(row.currency).toUpperCase() === normalizedFilter);
    return filteredByAsset.filter((row) => activeCurrencies.has(String(row.currency).toUpperCase()));
  }, [portfolioRows, normalizedFilter, activeCurrencies]);

  const visibleFuturesRows = useMemo(() => {
    const filteredByAsset = normalizedFilter === 'ALL'
      ? futuresRows
      : futuresRows.filter((row) => String(row.currency).toUpperCase() === normalizedFilter);
    return filteredByAsset.filter((row) => activeCurrencies.has(String(row.currency).toUpperCase()));
  }, [futuresRows, normalizedFilter, activeCurrencies]);

  const optionsSummary = useMemo(() => buildSectionSummary({ rows: visiblePositionRows, getBias: (row) => row.summaryBias, t }), [t, visiblePositionRows]);
  const futuresSummary = useMemo(() => buildSectionSummary({ rows: visibleFuturesRows, getBias: (row) => row.summaryBias, t }), [t, visibleFuturesRows]);
  const portfolioSummary = useMemo(() => buildSectionSummary({ rows: visiblePortfolioRows, getBias: (row) => row.portfolio?.bias, t }), [t, visiblePortfolioRows]);

  const handleToggleSection = (sectionId) => {
    setActiveSection((current) => (current === sectionId ? null : sectionId));
  };

  if (loading) {
    return (
      <Card className="overflow-hidden border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(247,147,26,0.12),transparent_32%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-portfolio-risk-loading-card">
        <CardContent className="px-6 py-10">
          <div className="min-h-[220px]">
            <GeniLoader size="md" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!visibleWalletItems.length && !visiblePositionRows.length && !visibleFuturesRows.length && !visiblePortfolioRows.length) return null;

  return (
    <Card className="overflow-hidden border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(247,147,26,0.12),transparent_32%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-portfolio-risk-card">
      <CardContent className="space-y-5 px-5 py-6 sm:px-6 sm:py-7">
        <div className="max-w-3xl space-y-3" data-testid="options-portfolio-risk-header">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/45" data-testid="options-portfolio-risk-eyebrow">{t('positions.portfolioRiskEyebrow')}</p>
            {demoMeta ? <Badge className="border-[#2775CA]/20 bg-[#2775CA]/12 text-[#B8D7FF]" data-testid="options-portfolio-risk-demo-badge">{t('common.demoDataset')}</Badge> : null}
          </div>
          <div>
            <h2 className="text-4xl font-black font-heading tracking-tight text-white sm:text-5xl" data-testid="options-portfolio-risk-title">{t('positions.portfolioRiskTitle')}</h2>
            <p className="mt-3 max-w-2xl text-sm text-white/65 sm:text-base" data-testid="options-portfolio-risk-description">{t('positions.portfolioRiskDescription')}</p>
          </div>
        </div>

        <section className="space-y-4 rounded-[24px] border border-white/10 bg-black/20 p-4 sm:p-5" data-testid="options-wallet-exposure-card">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/45" data-testid="options-wallet-exposure-label">{t('positions.walletExposure')}</p>
              <p className="text-sm text-white/60" data-testid="options-wallet-exposure-helper">{t('positions.walletExposureHelper')}</p>
              <p className="text-xs uppercase tracking-[0.16em] text-white/40" data-testid="options-wallet-exposure-helper-secondary">{t('positions.walletExposureLegend')}</p>
            </div>

            <button
              type="button"
              onClick={() => setWalletExpanded((current) => !current)}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
              data-testid="options-wallet-exposure-toggle"
              aria-expanded={walletExpanded}
            >
              <span>{walletExpanded ? t('positions.walletExposureCollapse') : t('positions.walletExposureExpand')}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${walletExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className="flex h-4 w-full overflow-hidden rounded-full border border-white/10 bg-white/[0.04]" data-testid="options-wallet-exposure-bar">
            {visibleWalletItems.map((item) => (
              <div
                key={item.currency}
                className={`h-full bg-gradient-to-r ${item.colorClassName}`}
                style={{ width: `${item.percent * 100}%` }}
                data-testid={`options-wallet-exposure-segment-${String(item.currency).toLowerCase()}`}
                title={`${item.currency} ${item.usdLabel}`}
              />
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="options-wallet-exposure-legend">
            {visibleWalletItems.map((item) => (
              <div key={item.currency} className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3" data-testid={`options-wallet-exposure-legend-${String(item.currency).toLowerCase()}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <OptionsAssetIcon assetKey={item.currency} size={18} />
                    <p className="text-sm font-semibold text-white" data-testid={`options-wallet-exposure-legend-${String(item.currency).toLowerCase()}-title`}>{item.currency}</p>
                  </div>
                  <span className="text-xs uppercase tracking-[0.16em] text-white/45" data-testid={`options-wallet-exposure-legend-${String(item.currency).toLowerCase()}-pct`}>{item.percentLabel}</span>
                </div>
                <p className="mt-2 text-sm font-semibold text-white tabular-nums" data-testid={`options-wallet-exposure-legend-${String(item.currency).toLowerCase()}-native`}><OptionsMonetaryText value={item.valueLabel} iconSize={15} /></p>
                <p className="mt-1 text-xs text-white/55 tabular-nums" data-testid={`options-wallet-exposure-legend-${String(item.currency).toLowerCase()}-usd`}>{item.usdLabel}</p>
              </div>
            ))}
          </div>

          {walletExpanded ? (
            <div className="space-y-3 rounded-[20px] border border-white/10 bg-white/[0.03] p-4" data-testid="options-wallet-exposure-directional-panel">
              <div className="space-y-1">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-testid="options-wallet-exposure-directional-title">{t('positions.walletExposureDirectionalTitle')}</p>
                <p className="text-sm text-white/60" data-testid="options-wallet-exposure-directional-helper">{t('positions.walletExposureDirectionalHelper')}</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="options-wallet-exposure-directional-grid">
                {visibleWalletItems.map((item) => (
                  <div key={item.currency} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`options-wallet-exposure-directional-${String(item.currency).toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <OptionsAssetIcon assetKey={item.currency} size={18} />
                      <p className="text-base font-semibold text-white">{item.currency}</p>
                    </div>
                    <p className="mt-3 text-lg font-semibold tabular-nums text-white" data-testid={`options-wallet-exposure-directional-${String(item.currency).toLowerCase()}-pct`}>
                      {item.percentLabel}
                    </p>
                    <Badge className={`${item.bias.badgeClassName} mt-3 uppercase`} data-testid={`options-wallet-exposure-directional-${String(item.currency).toLowerCase()}-bias`}>
                      {t(item.bias.labelKey)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="overflow-hidden rounded-[24px] border border-white/10 bg-black/20" data-testid="options-portfolio-risk-accordion">
          <RiskAccordionSection
            title={t('positions.optionsExposure')}
            helper={t('positions.optionsExposureHelper')}
            summary={optionsSummary}
            expanded={activeSection === 'options'}
            onToggle={() => handleToggleSection('options')}
            testIdPrefix="options-position-exposure"
          >
            {visiblePositionRows.length ? (
              <div className="grid gap-4 xl:grid-cols-2" data-testid="options-position-risk-grid">
                {visiblePositionRows.map((row) => (
                  <div key={row.currency} className="rounded-[20px] border border-white/10 bg-white/[0.03] p-4" data-testid={`options-position-risk-card-${String(row.currency).toLowerCase()}`}>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <OptionsAssetIcon assetKey={row.currency} size={18} />
                        <p className="text-xl font-black font-heading text-white" data-testid={`options-position-risk-currency-${String(row.currency).toLowerCase()}`}>{row.currency}</p>
                      </div>
                      <p className="text-sm text-white/55" data-testid={`options-position-risk-helper-${String(row.currency).toLowerCase()}`}>{t('positions.positionRiskByCurrency')}</p>
                    </div>

                    <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid={`options-position-risk-metrics-${String(row.currency).toLowerCase()}`}>
                      <MetricCard label={t('positions.netDelta')} value={row.netDelta} testId={`options-position-risk-net-delta-${String(row.currency).toLowerCase()}`} />
                      <MetricCard label={t('positions.optionsExposure')} value={row.exposurePct === null ? t('positions.exposureUnavailable') : row.exposureLabel} testId={`options-position-risk-exposure-${String(row.currency).toLowerCase()}`} />
                      <MetricCard
                        label={t('positions.biasLabel')}
                        valueNode={<Badge className={`${row.summaryBias.badgeClassName} mt-2 w-fit uppercase`} data-testid={`options-position-risk-bias-${String(row.currency).toLowerCase()}`}>{row.summaryBias.labelKey ? t(row.summaryBias.labelKey) : '—'}</Badge>}
                        testId={`options-position-risk-bias-card-${String(row.currency).toLowerCase()}`}
                      />
                    </div>

                    <div className="mt-4 space-y-3" data-testid={`options-position-risk-scale-${String(row.currency).toLowerCase()}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-testid={`options-position-risk-scale-label-${String(row.currency).toLowerCase()}`}>{t('positions.biasLabel')}</p>
                        <p className="text-xs text-white/55" data-testid={`options-position-risk-scale-helper-${String(row.currency).toLowerCase()}`}>
                          {row.exposurePct === null ? t('positions.positionExposurePendingFormula') : t('positions.positionExposureFormula')}
                        </p>
                      </div>

                      <BiasScale
                        exposurePct={row.exposurePct}
                        markerPosition={row.markerPosition}
                        testIdPrefix={`options-position-risk-scale-${String(row.currency).toLowerCase()}`}
                        t={t}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : <AccordionEmptyState testId="options-position-exposure-empty">{t('positions.riskSectionNoData')}</AccordionEmptyState>}
          </RiskAccordionSection>

          <RiskAccordionSection
            title={t('positions.totalExposure')}
            helper={t('positions.totalExposureHelper')}
            summary={portfolioSummary}
            expanded={activeSection === 'total'}
            onToggle={() => handleToggleSection('total')}
            testIdPrefix="options-portfolio-exposure"
            withBorderTop
          >
            {visiblePortfolioRows.length ? (
              <div className="grid gap-4 xl:grid-cols-2" data-testid="options-portfolio-exposure-grid">
                {visiblePortfolioRows.map((row) => (
                  <div key={row.currency} className="rounded-[20px] border border-white/10 bg-white/[0.03] p-4" data-testid={`options-portfolio-exposure-card-${String(row.currency).toLowerCase()}`}>
                    <div className="flex items-center gap-2" data-testid={`options-portfolio-exposure-header-${String(row.currency).toLowerCase()}`}>
                      <OptionsAssetIcon assetKey={row.currency} size={18} />
                      <p className="text-xl font-black font-heading text-white">{row.currency}</p>
                    </div>

                    <div className="mt-4 grid gap-2 lg:grid-cols-4" data-testid={`options-portfolio-exposure-metrics-${String(row.currency).toLowerCase()}`}>
                      <SourceCard
                        label={t('positions.walletExposure')}
                        primary={row.wallet.nativeLabel}
                        secondary={row.wallet.exposureLabel}
                        badgeLabel={t(row.wallet.bias.labelKey)}
                        badgeClassName={row.wallet.bias.badgeClassName}
                        testId={`options-portfolio-exposure-wallet-${String(row.currency).toLowerCase()}`}
                      />
                      <SourceCard
                        label={t('positions.optionsExposure')}
                        primary={row.options.netDeltaLabel}
                        secondary={row.options.exposureLabel}
                        badgeLabel={row.options.bias.labelKey ? t(row.options.bias.labelKey) : '—'}
                        badgeClassName={row.options.bias.badgeClassName}
                        testId={`options-portfolio-exposure-options-${String(row.currency).toLowerCase()}`}
                      />
                      <SourceCard
                        label={t('positions.futuresExposure')}
                        primary={row.futures.netDeltaLabel}
                        secondary={row.futures.exposureLabel}
                        badgeLabel={row.futures.bias.labelKey ? t(row.futures.bias.labelKey) : '—'}
                        badgeClassName={row.futures.bias.badgeClassName}
                        testId={`options-portfolio-exposure-futures-${String(row.currency).toLowerCase()}`}
                      />
                      <SourceCard
                        label={t('positions.totalExposure')}
                        primary={row.portfolio.nativeLabel}
                        secondary={row.portfolio.exposureLabel}
                        badgeLabel={row.portfolio.bias.labelKey ? t(row.portfolio.bias.labelKey) : '—'}
                        badgeClassName={row.portfolio.bias.badgeClassName}
                        emphasis
                        testId={`options-portfolio-exposure-total-${String(row.currency).toLowerCase()}`}
                      />
                    </div>

                    <div className="mt-4 space-y-3" data-testid={`options-portfolio-exposure-scale-${String(row.currency).toLowerCase()}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-testid={`options-portfolio-exposure-scale-label-${String(row.currency).toLowerCase()}`}>{t('positions.biasLabel')}</p>
                        <p className="text-xs text-white/55" data-testid={`options-portfolio-exposure-scale-helper-${String(row.currency).toLowerCase()}`}>{t('positions.portfolioExposureScaleHelper')}</p>
                      </div>

                      <BiasScale
                        exposurePct={row.portfolio.exposurePct}
                        markerPosition={row.portfolio.markerPosition}
                        testIdPrefix={`options-portfolio-exposure-scale-${String(row.currency).toLowerCase()}`}
                        t={t}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : <AccordionEmptyState testId="options-portfolio-exposure-empty">{t('positions.riskSectionNoData')}</AccordionEmptyState>}
          </RiskAccordionSection>
        </section>
      </CardContent>
    </Card>
  );
};

const RiskAccordionSection = ({
  title,
  helper,
  summary,
  expanded,
  onToggle,
  children,
  testIdPrefix,
  withBorderTop = false,
}) => (
  <div className={withBorderTop ? 'border-t border-white/10' : ''} data-testid={`${testIdPrefix}-section`}>
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-white/[0.02] sm:flex-row sm:items-center sm:justify-between"
      data-testid={`${testIdPrefix}-accordion-button`}
      aria-expanded={expanded}
    >
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-testid={`${testIdPrefix}-title`}>{title}</p>
        <p className="text-sm text-white/60" data-testid={`${testIdPrefix}-helper`}>{helper}</p>
      </div>

      <div className="flex min-w-0 items-center gap-3 sm:max-w-[52%]">
        <p className="min-w-0 flex-1 text-sm text-white/68 sm:text-right" data-testid={`${testIdPrefix}-summary`}>{summary}</p>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/55" data-testid={`${testIdPrefix}-chevron`}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </div>
    </button>

    {expanded ? (
      <div className="border-t border-white/10 px-4 pb-4 pt-4" data-testid={`${testIdPrefix}-accordion-panel`}>
        {children}
      </div>
    ) : null}
  </div>
);

const AccordionEmptyState = ({ children, testId }) => (
  <div className="rounded-[18px] border border-dashed border-white/10 bg-white/[0.03] px-4 py-4 text-sm text-white/55" data-testid={testId}>
    {children}
  </div>
);

const BiasScale = ({ exposurePct, markerPosition, testIdPrefix, t }) => (
  <>
    <div className="relative">
      <div className={`grid h-4 grid-cols-5 overflow-hidden rounded-full border ${exposurePct === null ? 'border-white/10 opacity-45' : 'border-white/10'}`} data-testid={`${testIdPrefix}-track`}>
        {POSITION_BIAS_SEGMENTS.map((segment) => (
          <div key={segment.key} className={segment.className} data-testid={`${testIdPrefix}-segment-${segment.key}`} />
        ))}
      </div>
      {markerPosition !== null ? (
        <div className="absolute top-1/2 h-7 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/70 bg-white shadow-[0_0_18px_rgba(255,255,255,0.45)]" style={{ left: `${markerPosition}%` }} data-testid={`${testIdPrefix}-marker`} />
      ) : null}
    </div>

    <div className="grid grid-cols-5 gap-1.5" data-testid={`${testIdPrefix}-legend`}>
      {POSITION_BIAS_SEGMENTS.map((segment) => (
        <p key={segment.key} className="text-[9px] uppercase leading-tight tracking-[0.12em] text-white/45" data-testid={`${testIdPrefix}-legend-${segment.key}`}>{t(segment.labelKey)}</p>
      ))}
    </div>
  </>
);

const SourceCard = ({ label, primary, secondary, badgeLabel, badgeClassName, emphasis = false, testId }) => (
  <div className={`rounded-[18px] border px-3 py-3 ${emphasis ? 'border-white/14 bg-white/[0.05]' : 'border-white/10 bg-black/20'}`} data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.16em] text-white/40" data-testid={`${testId}-label`}>{label}</p>
    <p className={`mt-2 text-sm font-semibold text-white tabular-nums ${emphasis ? 'sm:text-base' : ''}`} data-testid={`${testId}-primary`}><OptionsMonetaryText value={primary} iconSize={15} /></p>
    <p className="mt-1 text-xs text-white/55" data-testid={`${testId}-secondary`}>{secondary}</p>
    <Badge className={`${badgeClassName} mt-3 uppercase`} data-testid={`${testId}-badge`}>{badgeLabel}</Badge>
  </div>
);

const MetricCard = ({ label, value = null, valueNode = null, testId }) => (
  <div className="rounded-[18px] border border-white/10 bg-black/20 px-3 py-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.16em] text-white/40">{label}</p>
    {valueNode || <p className="mt-2 text-sm font-semibold text-white tabular-nums" data-testid={`${testId}-value`}><OptionsMonetaryText value={value} iconSize={15} /></p>}
  </div>
);

export default OptionsPortfolioRisk;